import React from 'react';
import { Modal, message } from 'antd';
import { Dispatch, Store } from 'redux';
import { ICareTeamAssignmentPreview } from '@liquid-state/pephealth-client';
import waitForStoreUpdate from '@utils/waitForStoreUpdate';
import {
  clearOrganisationUserCareTeamAssignmentPreview,
  fetchOrganisationUserCareTeamAssignmentPreview,
} from '@redux/organisationUserCareTeamPreview/actions';
import {
  selectOrganisationUserCareTeamAssignmentPreview,
  selectOrganisationUserCareTeamAssignmentPreviewFailed,
  selectOrganisationUserCareTeamAssignmentPreviewLoading,
} from '@redux/organisationUserCareTeamPreview/reducers';
import CareTeamAssignmentImpactContent from './CareTeamAssignmentImpactContent';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export type SimpleConfirmOptions = {
  title: string;
  okText: string;
  okType?: 'danger' | 'primary' | 'ghost' | 'dashed' | 'link' | 'default';
};

export type ConfirmCareTeamAssignmentImpactOptions = {
  organisationSlug: string | undefined;
  userId: string | number;
  dispatch: Dispatch;
  store: Store;
  t: Translate;
  onConfirm: () => void | Promise<unknown>;
  whenEmpty: 'simple-confirm' | 'proceed';
  simpleConfirm?: SimpleConfirmOptions;
};

function showImpactModal(
  assignments: ICareTeamAssignmentPreview[],
  t: Translate,
  onConfirm: () => void | Promise<unknown>,
  onCancel: () => void,
) {
  return new Promise<void>(resolve => {
    Modal.confirm({
      title: t('common:careTeamAssignmentImpact.title'),
      content: <CareTeamAssignmentImpactContent assignments={assignments} t={t} />,
      okText: t('common:careTeamAssignmentImpact.ok'),
      okType: 'danger',
      onOk: () => Promise.resolve(onConfirm()).finally(resolve),
      onCancel: () => {
        onCancel();
        resolve();
      },
    });
  });
}

function showSimpleConfirm(
  simpleConfirm: SimpleConfirmOptions,
  onConfirm: () => void | Promise<unknown>,
) {
  return new Promise<void>(resolve => {
    Modal.confirm({
      title: simpleConfirm.title,
      okText: simpleConfirm.okText,
      okType: simpleConfirm.okType,
      onOk: () => Promise.resolve(onConfirm()).finally(resolve),
      onCancel: () => resolve(),
    });
  });
}

export default async function confirmCareTeamAssignmentImpact({
  organisationSlug,
  userId,
  dispatch,
  store,
  t,
  onConfirm,
  whenEmpty,
  simpleConfirm,
}: ConfirmCareTeamAssignmentImpactOptions): Promise<void> {
  if (!organisationSlug) {
    message.error(t('common:careTeamAssignmentImpact.missingOrganisationSlug'));
    return;
  }

  dispatch(fetchOrganisationUserCareTeamAssignmentPreview(organisationSlug, Number(userId)));
  await waitForStoreUpdate(store, selectOrganisationUserCareTeamAssignmentPreviewLoading);

  const state = store.getState();
  if (selectOrganisationUserCareTeamAssignmentPreviewFailed(state)) {
    dispatch(clearOrganisationUserCareTeamAssignmentPreview());
    return;
  }

  const assignments = selectOrganisationUserCareTeamAssignmentPreview(state);

  const runConfirm = async () => {
    try {
      await onConfirm();
    } finally {
      dispatch(clearOrganisationUserCareTeamAssignmentPreview());
    }
  };

  if (assignments.length === 0) {
    if (whenEmpty === 'proceed') {
      await runConfirm();
      return;
    }
    if (!simpleConfirm) {
      dispatch(clearOrganisationUserCareTeamAssignmentPreview());
      return;
    }
    await showSimpleConfirm(simpleConfirm, runConfirm);
    dispatch(clearOrganisationUserCareTeamAssignmentPreview());
    return;
  }

  await showImpactModal(assignments, t, runConfirm, () => {
    dispatch(clearOrganisationUserCareTeamAssignmentPreview());
  });
}
